import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { extractDateFromISO } from '../../utils/validation';
import type { PackingSlip, ShipPackingSlipInput } from '../../types';
import { CARRIERS } from '../../types';

interface ShipPackingSlipModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ShipPackingSlipInput) => Promise<void>;
  packingSlip: PackingSlip | null;
  isLoading?: boolean;
}

function getTodayDate(): string {
  return extractDateFromISO(new Date().toISOString());
}

export function ShipPackingSlipModal({
  open,
  onClose,
  onSubmit,
  packingSlip,
  isLoading,
}: ShipPackingSlipModalProps) {
  const [carrier, setCarrier] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [shipDate, setShipDate] = useState(getTodayDate());

  useEffect(() => {
    if (open && packingSlip) {
      setCarrier(packingSlip.carrier || '');
      setServiceType(packingSlip.service_type || '');
      setTrackingNumber(packingSlip.tracking_number || '');
      setShippingCost(packingSlip.shipping_cost?.toString() || '');
      setShipDate(packingSlip.ship_date || getTodayDate());
    }
  }, [open, packingSlip]);

  const selectedCarrier = CARRIERS.find((c) => c.value === carrier);
  const serviceOptions = selectedCarrier?.services || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!carrier || !trackingNumber) return;

    // Safely parse shipping cost
    let parsedCost: number | undefined;
    if (shippingCost) {
      const cost = parseFloat(shippingCost);
      if (!isNaN(cost) && isFinite(cost) && cost >= 0) {
        parsedCost = cost;
      }
    }

    try {
      await onSubmit({
        carrier,
        service_type: serviceType || undefined,
        tracking_number: trackingNumber,
        shipping_cost: parsedCost,
        ship_date: shipDate || undefined,
      });
      toast.success('Packing slip shipped successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to ship packing slip: ${message}`);
      console.error('Error shipping packing slip:', error);
      // Keep form open for retry
    }
  };

  const handleClose = () => {
    setCarrier('');
    setServiceType('');
    setTrackingNumber('');
    setShippingCost('');
    setShipDate(getTodayDate());
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Ship Packing Slip</DialogTitle>
          <DialogDescription>
            Enter shipping details to mark this packing slip as shipped.
            {packingSlip && (
              <span className="block mt-1 font-mono text-foreground">
                {packingSlip.slip_number}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="carrier">Carrier *</Label>
              <Select value={carrier} onValueChange={(v) => { setCarrier(v); setServiceType(''); }}>
                <SelectTrigger id="carrier">
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent>
                  {CARRIERS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="serviceType">Service Type</Label>
              <Select
                value={serviceType}
                onValueChange={setServiceType}
                disabled={!carrier || serviceOptions.length === 0}
              >
                <SelectTrigger id="serviceType">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="trackingNumber">Tracking Number *</Label>
            <Input
              id="trackingNumber"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Enter tracking number"
              className="font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shipDate">Ship Date</Label>
              <Input
                id="shipDate"
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shippingCost">Shipping Cost</Label>
              <Input
                id="shippingCost"
                type="number"
                step="0.01"
                min="0"
                value={shippingCost}
                onChange={(e) => setShippingCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !carrier || !trackingNumber}
            >
              {isLoading ? 'Shipping...' : 'Ship'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
